import { addWeeks } from "date-fns"
import { useMemo } from "react"

import { api, type RouterOutputs } from "~/utils/api"
import { convertToEndOfMonthUTC, convertToEndOfWeekUTC } from "~/utils/datetime"
import {
  aggregateWeekly,
  buildExpectedDemandByVariant,
  buildSalesByVariant,
  computePerformance,
  computeSummaryFromAggregated,
  computeVariantStats,
  PERF_WEEKDIFF_MARGIN,
  type PerformanceSummary,
  type PerformanceVariantStats,
} from "~/utils/forecastPerformance"

export type { PerformanceSummary, PerformanceVariantStats }
export { PERF_WEEKDIFF_MARGIN }

type AggregatedSales = RouterOutputs["sales"]["getAggregatedSales"]

type Params = {
  variantIds: string[]
  warehouseId: string | undefined
  startDate: Date
  endDate: Date
  monthlyView: boolean
  enabled?: boolean
}

/**
 * Joins per-variant historic forecast (expected demand) with actual sales.
 *
 * Past-only: future weeks are discarded. Sales are always fetched at weekly
 * granularity so the overlap math matches the spec ("overlapping weeks where
 * both sales and historic forecast data are available"), regardless of view.
 * Monthly conversion is applied only to the chart/table-facing maps.
 *
 * The join lives in `~/utils/forecastPerformance` (`computePerformance`), shared with Haki's
 * `get_product_performance` tool.
 */
export function usePerformanceData({
  variantIds,
  warehouseId,
  startDate,
  endDate,
  monthlyView,
  enabled = true,
}: Params) {
  const queryEnabled = enabled && variantIds.length > 0

  const { data: performanceMap, isLoading: loadingPerformance } =
    api.forecasts.getPerformanceForVariants.useQuery(
      { variantIds, warehouseId, fromDate: addWeeks(startDate, -PERF_WEEKDIFF_MARGIN) },
      { enabled: queryEnabled },
    )

  // Match the interval AND the exact param shape that `useAggregatedSales`
  // uses (notably the end-of-period wrapping on endDate) so React Query treats
  // this and the VariantGraph's sales fetch as the same query key and dedupes
  // them into a single network call. In monthly view the API (SaleMonthly)
  // does not contain partial current-month data, so the table and tile both
  // naturally exclude it.
  const salesQueryEndDate = monthlyView
    ? convertToEndOfMonthUTC(endDate)
    : convertToEndOfWeekUTC(endDate)
  const { data: rawSales, isLoading: loadingSales } = api.sales.getAggregatedSales.useQuery(
    {
      variantIds,
      startDate,
      endDate: salesQueryEndDate,
      warehouseId,
      interval: monthlyView ? "month" : "week",
    },
    { enabled: queryEnabled },
  )

  const isLoading = loadingPerformance || loadingSales

  const result = useMemo(
    () =>
      computePerformance({
        performanceMap,
        rawSales: rawSales as AggregatedSales | undefined,
        variantIds,
        startDate,
        endDate,
        monthlyView,
      }),
    [performanceMap, rawSales, variantIds, startDate, endDate, monthlyView],
  )

  return {
    ...result,
    isLoading,
    hasData: !isLoading && result.summary.overlapPeriods > 0,
  }
}

// Exported for unit tests
export const __test = {
  buildExpectedDemandByVariant,
  buildSalesByVariant,
  computeVariantStats,
  computeSummaryFromAggregated,
  aggregateWeekly,
}
